import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  AlertTitle,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import React, { FormEvent, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/auths/useAuthService';

export default function Login() {
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [loginFailed, setLoginFailed] = useState<boolean>(false);
  const { isAuthenticated, login } = useAuth();
  const [searchParams] = useSearchParams();
  const redirectPath = searchParams.get('redirect_to') || '/';
  const navigate = useNavigate();
  const { palette, breakpoints } = useTheme();

  useEffect(() => {
    if (isAuthenticated()) {
      navigate(redirectPath);
    }
    // Only check on page load, don't have to react to changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    login({ username, password })
      .then(() => {
        navigate(redirectPath);
      })
      .catch(() => {
        setLoginFailed(true);
      });
  };

  const isMobile = useMediaQuery(breakpoints.up(700));

  return (
    <>
      <main className="app-content">
        <Box
          sx={{
            height: '95vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              flexGrow: 1,
              alignSelf: 'center',
              padding: '24px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                m: '0 auto',
              }}
            >
              {loginFailed && (
                <Alert
                  severity="error"
                  action={
                    <IconButton
                      aria-label="close"
                      color="inherit"
                      size="small"
                      onClick={() => {
                        setLoginFailed(false);
                      }}
                    >
                      <CloseIcon fontSize="inherit" />
                    </IconButton>
                  }
                  sx={{ mb: 2 }}
                >
                  <AlertTitle>Login Failed!</AlertTitle>
                  Please check your credentials and try again.
                </Alert>
              )}
              <Box
                sx={{
                  width: isMobile ? 400 : 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  padding: isMobile ? '32px' : '0',
                }}
              >
                <Box
                  component="form"
                  onSubmit={handleSubmit}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    p: 2,
                  }}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <img src="assets/adpclear-updated-logo.png" height="52px" alt="logo" />
                  </Box>
                  <TextField
                    label="Username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    fullWidth
                    variant="standard"
                  />
                  <TextField
                    label="Password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    fullWidth
                    variant="standard"
                  />
                  <Button type="submit" variant="contained" color="primary" fullWidth>
                    Login
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
              position: 'relative',
              marginTop: 'auto',
              pb: 4,
            }}
          >
            <Typography
              sx={{
                color: palette.text.secondary,
                mb: 0.5,
              }}
            >
              All rights reserved. Copyright © 2024
            </Typography>
            <Typography
              sx={{
                color: palette.text.secondary,
              }}
            >
              Privacy Policy | Terms & Conditions
            </Typography>
          </Box>
        </Box>
      </main>
    </>
  );
}
